import Calendar from './components/Calendar';
export default class DateRangePicker {
    constructor() {
        this.FOCUS_NEXT_INPUT_DELAY = 50;
        this.MOBILE_CLICK_DELAY = 100;
        this.DEBOUNCE_TIMEOUT = 100;
        this.DOM_UPDATE_DELAY = 10;
        this.tempStartDate = null;
        this.tempEndDate = null;
        this.originalStartDate = '';
        this.originalEndDate = '';
        this.applyClicked = false;
        this.observer = null;
        this.isPickerOpen = false;
        this.documentClickHandler = null;
        this.documentKeydownHandler = null;
        this.dateFromField = document.getElementById('date_from');
        this.dateToField = document.getElementById('date_to');
        if (this.dateFromField && this.dateToField) {
            this.initCalendarPicker();
        }
    }
    initCalendarPicker() {
        const calendar = new Calendar({
            field: this.dateFromField,
            secondField: this.dateToField,
            singleDate: false,
            format: 'MM / DD / YYYY',
            footer: true,
            onSelect: (start, end) => {
                this.tempStartDate = start;
                this.tempEndDate = end;
            },
            onClose: () => {
                this.tempStartDate = null;
                this.tempEndDate = null;
                if (!this.applyClicked) {
                    this.restoreOriginalValues();
                }
                this.applyClicked = false;
            },
            onOpen: () => {
                this.storeOriginalValues();
                this.addFooterEventListeners();
                this.movePickerToContainer();
                this.isPickerOpen = true;
                this.attachClickOutsideListener();
            }
        });
        this.picker = calendar.getPicker();
    }
    attachClickOutsideListener() {
        this.addInputFocusListeners();
        this.documentClickHandler = (event) => {
            var _a;
            const target = event.target;
            const pickerElement = document.querySelector('.lightpick');
            const calendarContainer = document.getElementById('calendar-container');
            const form = (_a = this.dateFromField) === null || _a === void 0 ? void 0 : _a.closest('form');
            const isClickOnInput = target === this.dateFromField || target === this.dateToField;
            const isClickOnPicker = pickerElement && pickerElement.contains(target);
            const isClickOnCalendarContainer = calendarContainer && calendarContainer.contains(target);
            const isClickOnButton = form && form.contains(target) && (target.tagName === 'BUTTON' || target.closest('button'));
            if (!isClickOnInput && !isClickOnPicker && !isClickOnCalendarContainer && !isClickOnButton) {
                this.hidePicker();
            }
        };
        this.documentKeydownHandler = (event) => {
            if (event.key === 'Escape') {
                this.hidePicker();
            }
            if (event.shiftKey && event.key === 'Tab') {
                // Defer the check until after focus has been updated
                setTimeout(() => {
                    const pickerElement = document.querySelector('.lightpick');
                    const calendarContainer = document.getElementById('calendar-container');
                    const activeElement = document.activeElement;
                    const isActiveElementInPicker = pickerElement && pickerElement.contains(activeElement);
                    const isActiveElementInCalendarContainer = calendarContainer && calendarContainer.contains(activeElement);
                    if (!isActiveElementInPicker && !isActiveElementInCalendarContainer && this.isPickerOpen) {
                        this.hidePicker();
                    }
                }, 0);
            }
        };
        document.addEventListener('click', this.documentClickHandler, true);
        document.addEventListener('keydown', this.documentKeydownHandler);
    }
    removeClickOutsideListener() {
        if (this.documentClickHandler) {
            document.removeEventListener('click', this.documentClickHandler, true);
            this.documentClickHandler = null;
        }
        if (this.documentKeydownHandler) {
            document.removeEventListener('keydown', this.documentKeydownHandler);
            this.documentKeydownHandler = null;
        }
    }
    showPicker() {
        const pickerElement = document.querySelector('.lightpick');
        if (pickerElement) {
            pickerElement.style.display = 'block';
        }
        this.isPickerOpen = true;
        this.storeOriginalValues();
        this.addFooterEventListeners();
        this.movePickerToContainer();
        this.attachClickOutsideListener();
    }
    hidePicker() {
        const pickerElement = document.querySelector('.lightpick');
        if (pickerElement) {
            pickerElement.style.display = 'none';
        }
        this.isPickerOpen = false;
        this.removeClickOutsideListener();
    }
    focusCalendarYearField() {
        const yearSelect = document.querySelector('select[name="calendar_year"]');
        if (yearSelect) {
            setTimeout(() => {
                yearSelect.focus();
            }, this.DOM_UPDATE_DELAY);
        }
    }
    addInputFocusListeners() {
        this.dateFromField.addEventListener('focus', () => {
            if (!this.isPickerOpen) {
                this.showPicker();
                this.focusCalendarYearField();
            }
        });
        this.dateToField.addEventListener('focus', () => {
            if (!this.isPickerOpen) {
                this.showPicker();
                this.focusCalendarYearField();
            }
        });
    }
    movePickerToContainer() {
        const pickerElement = document.querySelector('.lightpick');
        const calendarContainer = document.getElementById('calendar-container');
        if (pickerElement && calendarContainer) {
            // Move to container if not already there
            if (pickerElement.parentElement !== calendarContainer) {
                calendarContainer.appendChild(pickerElement);
            }
            // Force inline styles to override Lightpick's absolute positioning
            pickerElement.setAttribute('style', `
                position: static !important;
                display: block !important;
                top: auto !important;
                left: auto !important;
                right: auto !important;
                bottom: auto !important;
            `);
        }
    }
    formatDateWithSpaces(date) {
        if (!date)
            return '';
        const month = date.format('MM');
        const day = date.format('DD');
        const year = date.format('YYYY');
        return `${month} / ${day} / ${year}`;
    }
    stopObserving() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }
    storeOriginalValues() {
        this.originalStartDate = this.dateFromField.value;
        this.originalEndDate = this.dateToField.value;
    }
    restoreOriginalValues() {
        this.dateFromField.value = this.originalStartDate;
        this.dateToField.value = this.originalEndDate;
        if (this.originalStartDate && this.originalEndDate) {
            this.picker.setDateRange(this.parseDate(this.originalStartDate), this.parseDate(this.originalEndDate));
        }
        else if (this.originalStartDate) {
            this.picker.setDate(this.parseDate(this.originalStartDate));
        }
        else {
            this.picker.setDate(null);
        }
    }
    parseDate(dateString) {
        if (dateString.includes('/')) {
            const [month, day, year] = dateString.split('/');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        return new Date(dateString);
    }
    getNextFilterInput(currentEl) {
        const form = this.dateFromField.closest('form');
        if (!form)
            return null;
        const filterInputs = Array.from(form.querySelectorAll('.form-field input, .form-field select'));
        const currentIndex = filterInputs.indexOf(currentEl);
        if (currentIndex !== -1 && currentIndex < filterInputs.length - 1) {
            return filterInputs[currentIndex + 1];
        }
        return null;
    }
    focusNextInputAndHidePicker() {
        const nextInput = this.getNextFilterInput(this.dateToField);
        if (nextInput) {
            setTimeout(() => {
                nextInput.focus();
                this.hidePicker();
            }, this.FOCUS_NEXT_INPUT_DELAY);
        }
    }
    addFooterEventListeners() {
        const footerElement = document.querySelector('.lightpick__footer');
        if (footerElement && !footerElement.querySelector('.custom-apply-btn')) {
            const applyLabel = this.getTranslatedLabel('apply') || 'Apply';
            const cancelLabel = this.getTranslatedLabel('cancel') || 'Cancel';
            footerElement.innerHTML = `
                <button type="button" class="custom-cancel-btn" value="cancel">${cancelLabel}</button>
                <button type="button" class="custom-apply-btn" value="ok">${applyLabel}</button>
            `;
            const applyButton = footerElement.querySelector('.custom-apply-btn');
            const cancelButton = footerElement.querySelector('.custom-cancel-btn');
            let applyProcessing = false;
            let cancelProcessing = false;
            const handleApplyClick = (event) => {
                if (applyProcessing)
                    return;
                applyProcessing = true;
                event.preventDefault();
                this.applyDateRange();
                setTimeout(() => {
                    applyProcessing = false;
                }, this.DEBOUNCE_TIMEOUT);
            };
            const handleCancelClick = (event) => {
                if (cancelProcessing)
                    return;
                cancelProcessing = true;
                event.preventDefault();
                this.cancelDateRange();
                setTimeout(() => {
                    cancelProcessing = false;
                }, this.DEBOUNCE_TIMEOUT);
            };
            applyButton.addEventListener('click', handleApplyClick);
            applyButton.addEventListener('pointerup', (event) => {
                setTimeout(() => {
                    handleApplyClick(event);
                }, this.MOBILE_CLICK_DELAY);
            });
            applyButton.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ' || event.key === 'Tab') {
                    event.preventDefault();
                    if (!applyProcessing) {
                        applyProcessing = true;
                        this.applyDateRange();
                        this.focusNextInputAndHidePicker();
                        setTimeout(() => {
                            applyProcessing = false;
                        }, this.DEBOUNCE_TIMEOUT);
                    }
                }
            });
            cancelButton.addEventListener('click', handleCancelClick);
            cancelButton.addEventListener('pointerup', (event) => {
                setTimeout(() => {
                    handleCancelClick(event);
                }, this.MOBILE_CLICK_DELAY);
            });
            cancelButton.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (!cancelProcessing) {
                        cancelProcessing = true;
                        this.cancelDateRange();
                        this.focusNextInputAndHidePicker();
                        setTimeout(() => {
                            cancelProcessing = false;
                        }, this.DEBOUNCE_TIMEOUT);
                    }
                }
            });
        }
    }
    getTranslatedLabel(labelType) {
        const form = this.dateFromField.closest('form');
        if (form) {
            const label = form.dataset[`datePicker${labelType.charAt(0).toUpperCase() + labelType.slice(1)}Label`];
            if (label)
                return label;
        }
        return '';
    }
    cancelDateRange() {
        this.dateFromField.value = '';
        this.dateToField.value = '';
        this.picker.setDate(null);
        this.tempStartDate = null;
        this.tempEndDate = null;
        this.applyClicked = true;
        this.hidePicker();
        this.submitForm();
    }
    applyDateRange() {
        if (this.tempStartDate) {
            this.dateFromField.value = this.formatDateWithSpaces(this.tempStartDate);
        }
        if (this.tempEndDate) {
            this.dateToField.value = this.formatDateWithSpaces(this.tempEndDate);
        }
        else if (this.tempStartDate && !this.tempEndDate) {
            this.dateToField.value = '';
        }
        this.applyClicked = true;
        this.tempStartDate = null;
        this.tempEndDate = null;
        this.submitForm();
    }
    submitForm() {
        const form = this.dateFromField.closest('form');
        if (form) {
            const changeEvent = new Event('change', { bubbles: true });
            this.dateFromField.dispatchEvent(changeEvent);
            this.hidePicker();
        }
    }
    destroy() {
        this.stopObserving();
        if (this.picker) {
            this.picker.destroy();
        }
    }
    setDateRange(startDate, endDate) {
        if (this.picker && startDate) {
            const start = this.parseDate(startDate);
            const end = endDate ? this.parseDate(endDate) : null;
            if (end) {
                this.picker.setDateRange(start, end);
            }
            else {
                this.picker.setDate(start);
            }
        }
    }
}
